import {
	cloneElement,
	createContext,
	isValidElement,
	useCallback,
	useContext,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type FocusEvent,
	type HTMLAttributes,
	type PointerEvent,
	type ReactElement,
	type ReactNode,
	type Ref,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "../cn.js";

const GAP = 6;
const VIEWPORT_MARGIN = 8;

export type TooltipSide = "top" | "bottom" | "left" | "right";

interface TooltipProviderValue {
	delayDuration: number;
}

const TooltipProviderContext = createContext<TooltipProviderValue>({ delayDuration: 300 });

export interface TooltipProviderProps {
	children: ReactNode;
	/** Delay before a pointer hover shows the tooltip (shadcn default is 700; Key Mail uses 300). */
	delayDuration?: number;
}

export function TooltipProvider({ children, delayDuration = 300 }: TooltipProviderProps) {
	const value = useMemo(() => ({ delayDuration }), [delayDuration]);
	return <TooltipProviderContext.Provider value={value}>{children}</TooltipProviderContext.Provider>;
}

interface TooltipContextValue {
	open: boolean;
	setOpen: (open: boolean) => void;
	triggerRef: React.RefObject<HTMLElement | null>;
	contentId: string;
	delayDuration: number;
	scheduleOpen: (instant?: boolean) => void;
	scheduleClose: () => void;
	clearTimers: () => void;
}

const TooltipContext = createContext<TooltipContextValue | null>(null);

function useTooltipContext(component: string) {
	const ctx = useContext(TooltipContext);
	if (!ctx) throw new Error(`${component} must be used within Tooltip`);
	return ctx;
}

export interface TooltipProps {
	children: ReactNode;
	open?: boolean;
	defaultOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
}

export function Tooltip({ children, open: controlledOpen, defaultOpen = false, onOpenChange }: TooltipProps) {
	const provider = useContext(TooltipProviderContext);
	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
	const open = controlledOpen ?? uncontrolledOpen;
	const triggerRef = useRef<HTMLElement | null>(null);
	const contentId = useId();
	const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const setOpen = useCallback(
		(next: boolean) => {
			if (controlledOpen !== undefined) {
				onOpenChange?.(next);
				return;
			}
			setUncontrolledOpen(next);
			onOpenChange?.(next);
		},
		[controlledOpen, onOpenChange],
	);

	const clearTimers = useCallback(() => {
		if (showTimerRef.current) {
			clearTimeout(showTimerRef.current);
			showTimerRef.current = null;
		}
		if (hideTimerRef.current) {
			clearTimeout(hideTimerRef.current);
			hideTimerRef.current = null;
		}
	}, []);

	const scheduleOpen = useCallback(
		(instant = false) => {
			clearTimers();
			if (instant) {
				setOpen(true);
				return;
			}
			showTimerRef.current = setTimeout(() => setOpen(true), provider.delayDuration);
		},
		[clearTimers, provider.delayDuration, setOpen],
	);

	const scheduleClose = useCallback(() => {
		clearTimers();
		hideTimerRef.current = setTimeout(() => setOpen(false), 0);
	}, [clearTimers, setOpen]);

	const value = useMemo(
		(): TooltipContextValue => ({
			open,
			setOpen,
			triggerRef,
			contentId,
			delayDuration: provider.delayDuration,
			scheduleOpen,
			scheduleClose,
			clearTimers,
		}),
		[open, setOpen, contentId, provider.delayDuration, scheduleOpen, scheduleClose, clearTimers],
	);

	return <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>;
}

function composeRefs<T>(...refs: Array<Ref<T> | undefined>) {
	return (node: T | null) => {
		for (const ref of refs) {
			if (!ref) continue;
			if (typeof ref === "function") ref(node);
			else ref.current = node;
		}
	};
}

export interface TooltipTriggerProps extends HTMLAttributes<HTMLElement> {
	asChild?: boolean;
	children: ReactElement;
}

export function TooltipTrigger({ asChild = false, children, ...props }: TooltipTriggerProps) {
	const ctx = useTooltipContext("TooltipTrigger");
	if (!asChild) {
		throw new Error("TooltipTrigger requires asChild with a single child element");
	}
	if (!isValidElement(children)) {
		throw new Error("TooltipTrigger child must be a valid React element");
	}

	const child = children as ReactElement<{ ref?: Ref<HTMLElement> } & HTMLAttributes<HTMLElement>>;

	return cloneElement(child, {
		...props,
		...child.props,
		ref: composeRefs(ctx.triggerRef, child.props.ref),
		onPointerEnter: (event: PointerEvent<HTMLElement>) => {
			child.props.onPointerEnter?.(event);
			props.onPointerEnter?.(event);
			ctx.scheduleOpen();
		},
		onPointerLeave: (event: PointerEvent<HTMLElement>) => {
			child.props.onPointerLeave?.(event);
			props.onPointerLeave?.(event);
			ctx.scheduleClose();
		},
		onPointerDown: (event: PointerEvent<HTMLElement>) => {
			child.props.onPointerDown?.(event);
			props.onPointerDown?.(event);
			ctx.clearTimers();
			ctx.setOpen(false);
		},
		onFocus: (event: FocusEvent<HTMLElement>) => {
			child.props.onFocus?.(event);
			props.onFocus?.(event);
			ctx.scheduleOpen(true);
		},
		onBlur: (event: FocusEvent<HTMLElement>) => {
			child.props.onBlur?.(event);
			props.onBlur?.(event);
			ctx.scheduleClose();
		},
		"aria-describedby": ctx.open ? ctx.contentId : undefined,
	});
}

export interface TooltipContentProps extends HTMLAttributes<HTMLDivElement> {
	side?: TooltipSide;
	sideOffset?: number;
}

function portalTargetFor(anchor: HTMLElement | null): HTMLElement {
	const dialog = anchor?.closest("dialog");
	if (dialog instanceof HTMLDialogElement && dialog.open) return dialog;
	return document.body;
}

export function TooltipContent({
	side = "top",
	sideOffset = GAP,
	className,
	children,
	...props
}: TooltipContentProps) {
	const ctx = useTooltipContext("TooltipContent");
	const contentRef = useRef<HTMLDivElement | null>(null);
	const [mounted, setMounted] = useState(false);

	useLayoutEffect(() => {
		if (!ctx.open) {
			setMounted(false);
			return;
		}
		setMounted(true);
	}, [ctx.open]);

	useLayoutEffect(() => {
		if (!ctx.open || !mounted) return;
		const panel = contentRef.current;
		const anchor = ctx.triggerRef.current;
		if (!panel || !anchor) return;

		const place = () => {
			const box = anchor.getBoundingClientRect();
			const rect = panel.getBoundingClientRect();
			const width = rect.width || panel.offsetWidth;
			const height = rect.height || panel.offsetHeight;

			const spaceTop = box.top - VIEWPORT_MARGIN - sideOffset;
			const spaceBottom = window.innerHeight - box.bottom - VIEWPORT_MARGIN - sideOffset;
			const spaceLeft = box.left - VIEWPORT_MARGIN - sideOffset;
			const spaceRight = window.innerWidth - box.right - VIEWPORT_MARGIN - sideOffset;

			let resolvedSide = side;
			if (side === "top" && height > spaceTop && spaceBottom > spaceTop) resolvedSide = "bottom";
			if (side === "bottom" && height > spaceBottom && spaceTop > spaceBottom) resolvedSide = "top";
			if (side === "left" && width > spaceLeft && spaceRight > spaceLeft) resolvedSide = "right";
			if (side === "right" && width > spaceRight && spaceLeft > spaceRight) resolvedSide = "left";

			let top = 0;
			let left = 0;
			switch (resolvedSide) {
				case "top":
					top = box.top - sideOffset - height;
					left = box.left + box.width / 2 - width / 2;
					break;
				case "bottom":
					top = box.bottom + sideOffset;
					left = box.left + box.width / 2 - width / 2;
					break;
				case "left":
					top = box.top + box.height / 2 - height / 2;
					left = box.left - sideOffset - width;
					break;
				case "right":
					top = box.top + box.height / 2 - height / 2;
					left = box.right + sideOffset;
					break;
			}

			left = Math.min(
				Math.max(VIEWPORT_MARGIN, left),
				Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN),
			);
			top = Math.min(
				Math.max(VIEWPORT_MARGIN, top),
				Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN),
			);

			panel.style.position = "fixed";
			panel.style.top = `${top}px`;
			panel.style.left = `${left}px`;
			panel.style.zIndex = "80";
		};

		place();
		const observer = new ResizeObserver(place);
		observer.observe(panel);
		window.addEventListener("resize", place);
		window.addEventListener("scroll", place, true);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", place);
			window.removeEventListener("scroll", place, true);
		};
	}, [ctx.open, ctx.triggerRef, mounted, side, sideOffset]);

	if (!ctx.open || !mounted) return null;

	return createPortal(
		<div
			{...props}
			ref={contentRef}
			id={ctx.contentId}
			role="tooltip"
			className={cn(
				"pointer-events-none max-w-xs rounded-(--radius-sm) px-2 py-1 text-xs font-medium",
				"bg-(--surface-inverse) text-(--color-ink-50)",
				"shadow-(--shadow-raised)",
				className,
			)}
		>
			{children}
		</div>,
		portalTargetFor(ctx.triggerRef.current),
	);
}
